import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import ThemeToggle from '../ui/ThemeToggle';

interface AuthLayoutProps {
    children: React.ReactNode;
    title: string;
    subtitle: string;
}

const AuthContainer = styled.div`
  min-height: 100vh;
  display: flex;
  position: relative;
  overflow: hidden;
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('/assets/images/auth-bg.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(0, 0, 0, 0.8) 50%,
      rgba(0, 0, 0, 0.9) 100%
    );
    z-index: 2;
  }
`;

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing[8]};
  position: relative;
  z-index: 3;
  color: ${theme.colors.white};

  @media (max-width: ${theme.breakpoints.lg}) {
    display: none;
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[8]};
`;

const LogoIcon = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, ${theme.colors.white}, ${theme.colors.primary[100]});
  border-radius: ${theme.borderRadius['2xl']};
  margin: 0 auto ${theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  font-weight: ${theme.typography.fontWeight.bold};
  box-shadow: ${theme.shadows.xl};
`;

const LogoText = styled.h1`
  font-size: ${theme.typography.fontSize['4xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.white};
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const LogoSubtext = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: rgba(255, 255, 255, 0.9);
  margin: ${theme.spacing[2]} 0 0 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const FeaturesList = styled.div`
  max-width: 400px;
  margin-top: ${theme.spacing[8]};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  padding: ${theme.spacing[3]};
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.lg};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const FeatureText = styled.div`
  h3 {
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.white};
    margin: 0 0 ${theme.spacing[1]} 0;
  }
  
  p {
    font-size: ${theme.typography.fontSize.sm};
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
  }
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[8]};
  position: relative;
  z-index: 3;
  min-height: 100vh;

  @media (max-width: ${theme.breakpoints.lg}) {
    flex: none;
    width: 100%;
  }
`;

const AuthCard = styled.div`
  width: 100%;
  max-width: 450px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: ${theme.borderRadius['2xl']};
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  padding: ${theme.spacing[8]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  }
`;

const ThemeToggleContainer = styled.div`
  position: absolute;
  top: ${theme.spacing[6]};
  right: ${theme.spacing[6]};
  z-index: 4;
`;

const AuthLayout: React.FC<AuthLayoutProps> = ({ children, title, subtitle }) => {
    return (
        <AuthContainer>
            <BackgroundImage />

            <LeftPanel>
                <Logo>
                    <LogoIcon>🛡️</LogoIcon>
                    <LogoText>Guard Security</LogoText>
                    <LogoSubtext>Professional Security Portal</LogoSubtext>
                </Logo>
            </LeftPanel>

            <RightPanel>
                <ThemeToggleContainer>
                    <ThemeToggle />
                </ThemeToggleContainer>

                <AuthCard>
                    <div style={{ textAlign: 'center', marginBottom: theme.spacing[6] }}>
                        <h1 style={{
                            fontSize: theme.typography.fontSize['3xl'],
                            fontWeight: theme.typography.fontWeight.bold,
                            color: theme.colors.white,
                            margin: '0 0 8px 0'
                        }}>
                            {title}
                        </h1>
                        <p style={{
                            fontSize: theme.typography.fontSize.lg,
                            color: 'rgba(255, 255, 255, 0.8)',
                            margin: 0
                        }}>
                            {subtitle}
                        </p>
                    </div>
                    {children}
                </AuthCard>
            </RightPanel>
        </AuthContainer>
    );
};

export default AuthLayout;







